import {useState, useCallback, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useToast} from 'react-native-toast-notifications';
import {uploadMinimize} from 'interface/hooks/effects/transfers';
import {useModalConflict} from 'interface/hooks/useModalConflict';
import {useModalWebUpload} from 'interface/hooks/useModalWebUpload';
import {useModalUpsellFeature} from 'interface/hooks/useModalUpsellFeature';
import {useModalUpsellStorage} from 'interface/hooks/useModalUpsellStorage';
import {Uploader, UploadResolutions, UploadSession, FileUpload} from 'utils/uploader';
import {sessionToken, uploadState, uploadSync, uploadWebSync, uploadGetWebFiles} from 'utils/mediafire';
import {isWeb, isTouch} from 'utils/platform';
import filesystem from 'extensions/filesystem';
import {slices} from 'store';

import * as app from 'store/slices/app';
import * as transfers from 'store/slices/transfers';

export function useUploader(folderKey: string) {
  const toast = useToast();
  const session = useRef<UploadSession>(null);
  const conflict = useRef<FileUpload>(null);
  const limits = useSelector(app.selectors.getLimits);
  // const header = useSelector(app.selectors.getHeader);
  const web = useSelector(transfers.selectors.getUploadWeb);
  const view = useSelector(transfers.selectors.getUploadView);
  const items = useSelector(transfers.selectors.getUploadItems);
  const files = useSelector(transfers.selectors.getUploadFiles);
  const queue = useSelector(transfers.selectors.getUploadQueue);
  const status = uploadState(items);
  const [activated, setActivated] = useState(false);
  const progress = {
    hashed: Math.max(0, Math.min(100, (status.hashed / status.size) * 100)),
    uploaded: Math.max(0, Math.min(100, (status.uploaded / status.size) * 100)),
  };

  const dispatch = useDispatch();
  const activate = useCallback(() => setActivated(true), [setActivated]);
  const minimize = useCallback(uploadMinimize(dispatch, true), []);
  const maximize = useCallback(uploadMinimize(dispatch, false), []);
  const noop = useCallback((e) => e.stopPropagation(), []);
  // const drop = useCallback(filesEffects.dragDrop(dispatch, null, folderKey, ''), [folderKey]);
  // const dragOver = useCallback(filesEffects.dragOver(), []);
  const [showUpsellWebUpload] = useModalUpsellFeature('pro', 'webupload');
  const [showUpsellStorage] = useModalUpsellStorage('upload');
  const modalWebUpload = useModalWebUpload(folderKey/*, (url?: string | Types.SyntheticEvent) => typeof url === 'string' ? url : ''*/);
  const modalConflict = useModalConflict((action: UploadResolutions) =>
    Uploader.handleConflict(conflict.current, session.current, action, true));

  // Create uploader
  useEffect(() => {
    const availableStorage = limits ? limits.storage.total - limits.storage.used : -1;
    session.current = Uploader.create(sessionToken, availableStorage);
  }, []);

  // Start the uploader
  const start = useCallback(() => {
    Uploader.start(session.current);
    activate();
  }, [session.current]);

  // Stop and close the uploader
  const exit = useCallback(() => {
    Uploader.abort(session.current);
    dispatch(slices.transfers.actions.upload({id: null}));
  }, [session.current]);

  // Add files to upload session
  const addFiles = useCallback((folder: string) => {
    return async () => {
      try {
        const files = await filesystem.pickFiles();
        Uploader.addFiles(files as any, folder, session.current);
        if (isTouch()) {
          Uploader.start(session.current);
          activate();
        }
      } catch (err) {}
    }
  }, [session.current]);

  // Add files to upload session (web input)
  const addFilesInput = useCallback((folder: string) => {
    return async (e: any) => {
      Uploader.addFiles(e.target.files, folder, session.current);
      if (isTouch()) {
        Uploader.start(session.current);
        activate();
      }
    }
  }, [session.current]);

  // Add image/video to upload session
  const addMedia = useCallback((folder: string) => {
    return async () => {
      try {
        const files = await filesystem.pickMedia();
        Uploader.addFiles(files as any, folder, session.current);
        if (isTouch()) {
          Uploader.start(session.current);
          activate();
        }
      } catch (err) {}
    }
  }, [session.current]);

  // Add folder of files to upload session
  const addFolder = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
    Uploader.addFiles(e.target.files, folderKey, session.current);
  }, [session.current]);

  // Add files from paste
  const paste = useCallback((e: ClipboardEvent) => {
    if (e.clipboardData.files && e.clipboardData.files.length)
      return Uploader.addFiles(e.clipboardData.files, folderKey, session.current);
    const items = e.clipboardData.items;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      try {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      } catch (e) {}
    }
    if (files.length) {
      Uploader.addFiles(files, folderKey, session.current);
    } else {
      const text = e.clipboardData.getData('text');
      if (text && text.match(/^(ftp|sftp|http|https)\:\/\//)) {
        // TODO: modalWebUpload.show(text);
      }
    }
  }, [dispatch, folderKey]);

  // Sync upload events
  useEffect(() => {
    Uploader.addListener('progress', (_, file) => {
      uploadSync(dispatch, file);
    }, session.current);
    Uploader.addListener('complete', (_, file) => {
      uploadSync(dispatch, file);
      filesystem.clearReceivedFiles();
    }, session.current);
    Uploader.addListener('conflict', (_, file) => {
      uploadSync(dispatch, file);
      conflict.current = file;
      modalConflict.open();
    }, session.current);
    Uploader.addListener('failure', (error, file) => {
      if (file.exceededStorage)
        showUpsellStorage();
      uploadSync(dispatch, file, error);
    }, session.current);
    Uploader.addListener('failureBatch', (error) => {
      const message = error.toString() === 'ReqError: TypeError: Failed to fetch'
        ? 'Upload interrupted due to network issues'
        : error.toString();
      Uploader.abort(session.current);
      toast.show(message, {type: 'danger'});
      dispatch(slices.transfers.actions.upload({id: null}));
    }, session.current);
  }, [folderKey, session.current]);

  // Sync web uploads
  useEffect(() => {
    const timer = setInterval(() => {
      if (web && web.length > 0) {
        uploadGetWebFiles(web[0]).then((e: any) => {
          if (e.webUploads && e.webUploads.length > 0) {
            uploadWebSync(dispatch, folderKey, e.webUploads);
            activate();
          }
        });
      }
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [folderKey, web]);

  // Drag & drop uploads
  useEffect(() => {
    if (files) {
      Uploader.addFiles(files, folderKey, session.current);
      if (!queue) {
        Uploader.start(session.current);
        activate();
      }
    }
  }, [files, queue, folderKey]);

  // Received files from other apps
  useEffect(() => {
    filesystem.receiveFiles().then(files => {
      if (files.length > 0) {
        Uploader.addFiles(files as any, folderKey, session.current);
      }
    });
  }, [folderKey, session.current]);

  // Clipboard uploads
  useEffect(() => {
    if (isWeb()) {
      window.addEventListener('paste', paste);
      return () => {
        window.removeEventListener('paste', paste);
      };
    }
    return () => {};
  }, []);

  // Prevent navigation
  useEffect(() => {
    if (isWeb()) {
      window.onbeforeunload = activated && !status.completed
        ? () => 'Are you sure you want to cancel your upload?'
        : null;
    }
    return () => {
      if (isWeb()) {
        window.onbeforeunload = null;
      }
    };
  }, [status, activated]);

  return {
    view,
    items,
    files,
    status,
    progress,
    activated,
    start,
    exit,
    noop,
    minimize,
    maximize,
    addMedia,
    addFolder,
    addFiles,
    addFilesInput,
    showUpsellWebUpload,
    modalWebUpload,
    modalConflict,
  };
}
